<template>
  <ModuleWrapper
    :moduleId="moduleId"
    :image="imageSrcPath('assets/themebase.svg')"
    title="Stage Background"
    class="tw-relative"
    @initializeData="initializeData"
  >
    <template #main>
      <div class="tw-mb-2 tw-block tw-text-xs">
        For the best results we recommend using high quality PNGs (without any transparencies) that subtly enhances the
        stage instead of distracting from it.
      </div>
      <div>
        <StudioSelect
          id="desktop-background-type"
          label="Desktop Background Type"
          :options="[
            { value: 'Image', label: 'Image' },
            { value: 'Solid Color', label: 'Solid Color' },
            { value: 'Gradient', label: 'Gradient' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedDesktopBackgroundType"
          @change="onSelectDesktopBackgroundType"
        />
        <ImageUploadButton
          v-if="selectedDesktopBackgroundType === 'Image'"
          label="Min of 1024x768, Max of 2400x1800"
          :image="desktopStageBackground"
          :acceptedTypes="['.ico', '.png', '.jpg', '.jpeg', '.webp']"
          :s3BucketPath="s3BucketPath"
          :imageValidation="backgroundImageValidationDesktop"
          required
          class="tw-mb-4"
          @changeImageUrl="onDesktopStageImageUrlChange"
        />
        <ConditionalColorPicker
          v-else-if="selectedDesktopBackgroundType === 'Solid Color'"
          id="desktop-background-color-picker"
          label="Desktop Background Color"
          type="color"
          class="tw-mb-4"
          :initialColor="desktopColor.style"
          @colorChange="onDesktopColorChange"
        />
        <StudioColorPickerInput
          v-else-if="selectedDesktopBackgroundType === 'Gradient'"
          id="desktop-background-gradient-picker"
          label="Desktop Background Gradient"
          type="gradient"
          class="tw-mb-4"
          :initialGradient="desktopGradient"
          @gradientChange="onDesktopGradientChange"
        />
      </div>
      <div>
        <StudioSelect
          id="desktop-background-type"
          label="Mobile Background Type"
          :options="[
            { value: 'Image', label: 'Image' },
            { value: 'Solid Color', label: 'Solid Color' },
            { value: 'Gradient', label: 'Gradient' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedMobileBackgroundType"
          @change="onSelectMobileBackgroundType"
        />
        <ImageUploadButton
          v-if="selectedMobileBackgroundType === 'Image'"
          label="Min of 1024x768, Max of 2400x1800"
          :image="mobileStageBackground"
          :acceptedTypes="['.ico', '.png', '.jpg', '.jpeg', '.webp']"
          :s3BucketPath="s3BucketPath"
          :imageValidation="backgroundImageValidationMobile"
          required
          class="tw-mb-4"
          @changeImageUrl="onMobileStageImageUrlChange"
        />
        <ConditionalColorPicker
          v-else-if="selectedMobileBackgroundType === 'Solid Color'"
          id="mobile-background-color-picker"
          label="Mobile Background Color"
          type="color"
          :initialColor="mobileColor.style"
          @colorChange="onMobileColorChange"
        />
        <StudioColorPickerInput
          v-else-if="selectedMobileBackgroundType === 'Gradient'"
          id="desktop-background-gradient-picker"
          label="Mobile Background Gradient"
          type="gradient"
          :initialGradient="mobileGradient"
          @gradientChange="onMobileGradientChange"
        />
      </div>
    </template>
  </ModuleWrapper>
</template>

<script>
import { mapMutations, mapGetters, mapState } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';
import ImageUploadButton from '../components/ImageUploadButton.vue';
import { color, gradient } from '../data/defaultValues';

import { GET_RESOURCES_PATH, BACKGROUND_IMG_VALIDATION_DESKTOP, BACKGROUND_IMG_VALIDATION_MOBILE } from '@/constants';
import ConditionalColorPicker from '@/studio/lookAndFeel/components/ConditionalColorPicker.vue';
import StudioColorPickerInput from '@/studioComponents/StudioColorPickerInput.vue';
import StudioSelect from '@/studioComponents/StudioSelect.vue';
import {
  isColorString,
  destructureRgbColor,
  destructureGradientColor,
  isUrlString,
  isGradientString,
  getRGBAstring,
  destructureUrl
} from '@/utils/utils.js';

const cloneDeep = require('lodash.clonedeep');

export default {
  name: 'StageBackground',
  components: {
    ModuleWrapper,
    StudioSelect,
    ConditionalColorPicker,
    StudioColorPickerInput,
    ImageUploadButton
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedDesktopBackgroundType: 'Image',
      selectedMobileBackgroundType: 'Image',
      s3BucketPath: `${GET_RESOURCES_PATH(this.$route.params.eventId)}`,
      desktopColor: color,
      mobileColor: color,
      desktopGradient: cloneDeep(gradient),
      mobileGradient: cloneDeep(gradient),
      backgroundImageValidationDesktop: BACKGROUND_IMG_VALIDATION_DESKTOP,
      backgroundImageValidationMobile: BACKGROUND_IMG_VALIDATION_MOBILE
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapState('branding', ['isCurrentModuleInValid', 'changesMadeInThemeObj']),
    isFormValid() {
      if (this.selectedDesktopBackgroundType === 'Image' && !this.desktopStageBackground) {
        return false;
      }
      if (this.selectedMobileBackgroundType === 'Image' && !this.mobileStageBackground) {
        return false;
      }

      return true;
    },
    desktopStageBackground() {
      const moduleKeys = ['event_background'];
      const { event_background } = this.getInitialModuleData(moduleKeys);
      return isUrlString(event_background) ? destructureUrl(event_background) : '';
    },
    mobileStageBackground() {
      const moduleKeys = ['mobile_event_background'];
      const { mobile_event_background } = this.getInitialModuleData(moduleKeys);
      return isUrlString(mobile_event_background) ? destructureUrl(mobile_event_background) : '';
    }
  },
  watch: {
    isFormValid(newInputs) {
      this.setCurrentModuleInvalid(!newInputs);
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid'
    }),
    initializeData() {
      const moduleKeys = ['event_background', 'mobile_event_background'];
      const { event_background: eventBackground, mobile_event_background: mobileEventBackground } =
        this.getInitialModuleData(moduleKeys);

      const isDesktopGradient = isGradientString(eventBackground);
      const isDesktopSolidColor = isColorString(eventBackground);
      const isMobileGradient = isGradientString(mobileEventBackground);
      const isMobileSolidColor = isColorString(mobileEventBackground);

      if (isDesktopGradient) {
        this.desktopGradient = destructureGradientColor(eventBackground);
        this.selectedDesktopBackgroundType = 'Gradient';
        this.desktopColor = color;
      } else if (isDesktopSolidColor) {
        this.desktopColor = destructureRgbColor(eventBackground);
        this.selectedDesktopBackgroundType = 'Solid Color';
        this.desktopGradient = cloneDeep(gradient);
      } else {
        this.selectedDesktopBackgroundType = 'Image';
        this.desktopColor = color;
        this.desktopGradient = cloneDeep(gradient);
      }

      if (isMobileGradient) {
        this.mobileGradient = destructureGradientColor(mobileEventBackground);
        this.selectedMobileBackgroundType = 'Gradient';
        this.mobileColor = color;
      } else if (isMobileSolidColor) {
        this.mobileColor = destructureRgbColor(mobileEventBackground);
        this.selectedMobileBackgroundType = 'Solid Color';
        this.mobileGradient = cloneDeep(gradient);
      } else {
        this.selectedMobileBackgroundType = 'Image';
        this.mobileColor = color;
        this.mobileGradient = cloneDeep(gradient);
      }
    },
    onDesktopColorChange(color) {
      if (color.alpha > 1) {
        color.alpha = 1;
        color.style = getRGBAstring(color);
      }

      this.desktopColor = color;
      this.setChangesMadeInThemeObj({
        event_background: color.style
      });
    },
    onMobileColorChange(color) {
      if (color.alpha > 1) {
        color.alpha = 1;
        color.style = getRGBAstring(color);
      }

      this.mobileColor = color;
      this.setChangesMadeInThemeObj({
        mobile_event_background: color.style
      });
    },
    onDesktopGradientChange(gradient) {
      this.desktopGradient = gradient;
      this.setChangesMadeInThemeObj({
        event_background: gradient.style
      });
    },
    onMobileGradientChange(gradient) {
      this.mobileGradient = gradient;
      this.setChangesMadeInThemeObj({
        mobile_event_background: gradient.style
      });
    },
    onSelectDesktopBackgroundType(e) {
      const selectedType = e.target.value;

      if (selectedType === 'Gradient') {
        this.setChangesMadeInThemeObj({
          event_background: this.desktopGradient.style
        });
      } else if (selectedType === 'Solid Color') {
        this.setChangesMadeInThemeObj({
          event_background: this.desktopColor.style
        });
      }

      this.selectedDesktopBackgroundType = selectedType;
    },
    onSelectMobileBackgroundType(e) {
      const selectedType = e.target.value;

      if (selectedType === 'Gradient') {
        this.setChangesMadeInThemeObj({
          mobile_event_background: this.mobileGradient.style
        });
      } else if (selectedType === 'Solid Color') {
        this.setChangesMadeInThemeObj({
          mobile_event_background: this.mobileColor.style
        });
      }

      this.selectedMobileBackgroundType = selectedType;
    },
    onDesktopStageImageUrlChange(url) {
      this.setChangesMadeInThemeObj({
        event_background: `Url('${url}')`
      });
    },
    onMobileStageImageUrlChange(url) {
      this.setChangesMadeInThemeObj({
        mobile_event_background: `Url('${url}')`
      });
    }
  }
};
</script>
